// ============================================
// Ảnh khách gửi kèm góp ý
// ============================================
// `photo_urls` là LINK TĨNH VĨNH VIỄN, KHÔNG hết hạn (BE trả lời 28/08/2026 —
// đính chính giả định cũ của FE về presigned URL). Hiện CHƯA nối MinIO:
// `POST /uploads/images/` ghi thẳng xuống `MEDIA_ROOT` của Django và trả
// `https://<host>/media/uploads/feedback/<uuid>.jpg`. Không có TTL để lo.
//
// Hai điều BE lưu ý, đều là bẫy cho người sau:
// (a) URL sinh bằng `request.build_absolute_uri` rồi LƯU NGUYÊN CẢ CHUỖI vào
//     DB — KHÔNG lưu object key riêng. Tức là DOMAIN nằm cứng trong từng bản
//     ghi: đổi host (hay chỉ đổi http -> https) là chết TOÀN BỘ ảnh cũ, và
//     không có cách nào dựng lại URL mới ngoài việc chạy migration sửa chuỗi.
// (b) Ngày chuyển sang MinIO presigned, mọi URL đã lưu chết ngay lập tức — lúc
//     ĐÓ câu hỏi TTL mới thành vấn đề thật. Bây giờ thì chưa.
//
// ⚠️ VẪN GIỮ nhánh "ảnh không tải được" (BE cũng khuyên vậy): link không hết
// hạn không có nghĩa là ảnh luôn tải được — file bị xoá, đổi domain theo (a),
// hay mạng chập chờn đều cho ra một `<img>` gãy.
//
// Vì thế mỗi ô ảnh:
// - có kích thước CỐ ĐỊNH, đặt trước khi ảnh tải xong (`<img>` gãy sẽ co về
//   16x16 và làm cả lưới xô lệch);
// - `onError` đổi sang khối thay thế, kèm link mở thẳng URL gốc để người xử lý
//   còn tự kiểm tra được là link chết hay chỉ mạng chập chờn.

import { useState } from 'react';
import { useFeedbackText } from '../feedbackText';

interface FeedbackPhotoGridProps {
  /** LUÔN là mảng theo contract staff — rỗng chứ không `null`. */
  photoUrls: string[];
  onZoom: (url: string) => void;
}

function BrokenImageIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="2.5" y="4" width="15" height="12" rx="2" />
      <path d="M2.5 13l4-3.5 3 2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 4l4 12" strokeLinecap="round" />
    </svg>
  );
}

function FeedbackPhoto({ url, index, onZoom }: { url: string; index: number; onZoom: (url: string) => void }) {
  const { tr } = useFeedbackText();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="flex h-28 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-2 text-center">
        <span className="text-slate-400">
          <BrokenImageIcon />
        </span>
        <span className="text-[11px] leading-tight text-slate-500">
          {tr('feedbackPhotoUnavailable', 'Image unavailable')}
        </span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-[11px] font-semibold text-primary-600 hover:underline"
        >
          {tr('feedbackPhotoOpenLink', 'Open link')}
        </a>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onZoom(url)}
      className="group relative h-28 overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
    >
      <img
        src={url}
        alt={tr('feedbackPhotoAlt', 'Customer photo {{index}}', { index: index + 1 })}
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-full w-full object-cover transition-transform group-hover:scale-105"
      />
    </button>
  );
}

export function FeedbackPhotoGrid({ photoUrls, onZoom }: FeedbackPhotoGridProps) {
  const { tr } = useFeedbackText();

  if (photoUrls.length === 0) {
    return <p className="text-sm text-slate-500">{tr('feedbackNoPhotos', 'No photos attached.')}</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {photoUrls.map((url, index) => (
        <FeedbackPhoto key={`${url}-${index}`} url={url} index={index} onZoom={onZoom} />
      ))}
    </div>
  );
}
